import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Loader2, Palette } from 'lucide-react';
import { useCreateGradingSettings, useUpdateGradingSettings } from '../../hooks/useGrading';

// Default color palette for custom scales
const DEFAULT_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

// Preset grading scales for quick selection
const PRESET_SCALES = {
  expectations: {
    name: 'Performance Expectations',
    levels: [
      { label: 'Exceeding', min: 80, color: '#22c55e', description: 'Above expectations' },
      { label: 'Meeting', min: 60, color: '#3b82f6', description: 'At expectations' },
      { label: 'Not Meeting', min: 0, color: '#ef4444', description: 'Below expectations' }
    ]
  },
  mastery: {
    name: 'Mastery Levels',
    levels: [
      { label: 'Mastered', min: 90, color: '#22c55e', description: 'Complete mastery' },
      { label: 'Proficient', min: 70, color: '#3b82f6', description: 'Meets standards' },
      { label: 'Developing', min: 50, color: '#f59e0b', description: 'Approaching standards' },
      { label: 'Beginning', min: 0, color: '#ef4444', description: 'Needs support' }
    ]
  },
  standards: {
    name: 'Standards-Based',
    levels: [
      { label: '4 - Exceeds', min: 85, color: '#22c55e', description: 'Exceeds standard' },
      { label: '3 - Meets', min: 65, color: '#3b82f6', description: 'Meets standard' },
      { label: '2 - Approaching', min: 45, color: '#f59e0b', description: 'Approaching standard' },
      { label: '1 - Beginning', min: 0, color: '#ef4444', description: 'Below standard' }
    ]
  }
};

const GradingSettingsModal = ({ classId, settings, onClose }) => {
  const isEditing = !!settings?.id;
  
  const [formData, setFormData] = useState({
    gradingEnabled: true,
    gradingScale: 'letter',
    customScaleName: 'Custom Scale',
    customScaleConfig: {},
    passingThreshold: 60,
    showLetterGrade: true,
    showPercentage: true,
    showRank: false,
    gradesVisibleToParents: true,
    showGradesImmediately: true,
    reportingPeriod: 'quarterly',
    gradeCategories: [
      { name: 'Participation', weight: 10 },
      { name: 'Homework', weight: 30 },
      { name: 'Quizzes', weight: 20 },
      { name: 'Tests', weight: 40 }
    ]
  });

  // State for custom scale builder
  const [customLevels, setCustomLevels] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [_showCustomBuilder, _setShowCustomBuilder] = useState(false);

  const createSettings = useCreateGradingSettings();
  const updateSettings = useUpdateGradingSettings();

  useEffect(() => {
    if (settings) {
      const newFormData = {
        gradingEnabled: settings.gradingEnabled ?? true,
        gradingScale: settings.gradingScale || 'letter',
        customScaleName: settings.customScaleName || 'Custom Scale',
        customScaleConfig: settings.customScaleConfig || {},
        passingThreshold: settings.passingThreshold || 60,
        showLetterGrade: settings.showLetterGrade ?? true,
        showPercentage: settings.showPercentage ?? true,
        showRank: settings.showRank ?? false,
        gradesVisibleToParents: settings.gradesVisibleToParents ?? true,
        showGradesImmediately: settings.showGradesImmediately ?? true,
        reportingPeriod: settings.reportingPeriod || 'quarterly',
        gradeCategories: settings.gradeCategories || [
          { name: 'Participation', weight: 10 },
          { name: 'Homework', weight: 30 },
          { name: 'Quizzes', weight: 20 },
          { name: 'Tests', weight: 40 }
        ]
      };
      setFormData(newFormData);
      
      // Initialize custom levels if custom scale
      if (settings.gradingScale === 'custom' && settings.customScaleConfig) {
        const levels = Object.entries(settings.customScaleConfig)
          .map(([label, config]) => ({
            label,
            min: typeof config === 'object' ? config.min : config,
            color: typeof config === 'object' ? config.color : DEFAULT_COLORS[0],
            description: typeof config === 'object' ? config.description : ''
          }))
          .sort((a, b) => b.min - a.min);
        setCustomLevels(levels);
      } else if (settings.gradingScale === 'expectations') {
        setCustomLevels(PRESET_SCALES.expectations.levels);
      }
    }
  }, [settings]);

  // Update custom levels when scale changes to expectations
  useEffect(() => {
    if (formData.gradingScale === 'expectations') {
      setCustomLevels(PRESET_SCALES.expectations.levels);
    }
  }, [formData.gradingScale]);

  const handleAddCategory = () => {
    setFormData({
      ...formData,
      gradeCategories: [
        ...formData.gradeCategories,
        { name: '', weight: 0 }
      ]
    });
  };

  const handleRemoveCategory = (index) => {
    const newCategories = formData.gradeCategories.filter((_, i) => i !== index);
    setFormData({ ...formData, gradeCategories: newCategories });
  };

  const handleCategoryChange = (index, field, value) => {
    const newCategories = [...formData.gradeCategories];
    newCategories[index] = { ...newCategories[index], [field]: value };
    setFormData({ ...formData, gradeCategories: newCategories });
  };

  // Custom Scale Builder Functions
  const handleAddCustomLevel = () => {
    const newLevel = {
      label: '',
      min: customLevels.length > 0 ? Math.max(0, customLevels[customLevels.length - 1].min - 20) : 60,
      color: DEFAULT_COLORS[customLevels.length % DEFAULT_COLORS.length],
      description: ''
    };
    setCustomLevels([...customLevels, newLevel]);
  };

  const handleRemoveCustomLevel = (index) => {
    setCustomLevels(customLevels.filter((_, i) => i !== index));
  };

  const handleCustomLevelChange = (index, field, value) => {
    const newLevels = [...customLevels];
    newLevels[index] = { ...newLevels[index], [field]: value };
    setCustomLevels(newLevels);
  };

  const handleApplyPreset = (presetKey) => {
    const preset = PRESET_SCALES[presetKey];
    if (preset) {
      setCustomLevels([...preset.levels]);
      setFormData({ ...formData, customScaleName: preset.name });
    }
  };

  const buildCustomScaleConfig = () => {
    const config = {};
    customLevels.forEach(level => {
      if (level.label.trim()) {
        config[level.label.trim()] = {
          min: parseInt(level.min) || 0,
          color: level.color,
          description: level.description
        };
      }
    });
    return config;
  };

  const totalWeight = formData.gradeCategories.reduce((sum, cat) => sum + (parseInt(cat.weight) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      passingThreshold: parseFloat(formData.passingThreshold)
    };

    // Add custom scale config if using custom or expectations scale
    if (formData.gradingScale === 'custom' || formData.gradingScale === 'expectations') {
      data.customScaleConfig = buildCustomScaleConfig();
    }

    try {
      if (isEditing) {
        await updateSettings.mutateAsync({ classId, data });
      } else {
        await createSettings.mutateAsync({ classId, data });
      }
      onClose();
    } catch (err) {
      console.error('Failed to save settings:', err);
    }
  };

  const isCustomScale = formData.gradingScale === 'custom' || formData.gradingScale === 'expectations';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">
            {isEditing ? 'Grading Settings' : 'Enable Grading'}
          </h2>
          <button onClick={onClose} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Enable Grading */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="gradingEnabled"
              checked={formData.gradingEnabled}
              onChange={(e) => setFormData({...formData, gradingEnabled: e.target.checked})}
              className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
            />
            <label htmlFor="gradingEnabled" className="font-medium text-gray-900">
              Enable Grading for this Class
            </label>
          </div>

          {formData.gradingEnabled && (
            <>
              {/* Grading Scale */}
              <div>
                <label className="form-label">Grading Scale</label>
                <select
                  value={formData.gradingScale}
                  onChange={(e) => setFormData({...formData, gradingScale: e.target.value})}
                  className="form-input"
                >
                  <option value="percentage">Percentage Only</option>
                  <option value="letter">Letter Grades (A-F)</option>
                  <option value="gpa_4">GPA 4.0 Scale</option>
                  <option value="gpa_5">GPA 5.0 Scale</option>
                  <option value="pass_fail">Pass/Fail</option>
                  <option value="expectations">Performance Expectations (Exceeding/Meeting/Not Meeting)</option>
                  <option value="custom">Custom Scale</option>
                  <option value="points">Points Only</option>
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  {formData.gradingScale === 'expectations' && 'Students are evaluated against grade-level expectations'}
                  {formData.gradingScale === 'custom' && 'Create your own grading levels with custom labels and colors'}
                </p>
              </div>

              {/* Custom Scale Builder */}
              {isCustomScale && (
                <div className="bg-purple-50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Palette className="text-purple-600" size={20} />
                      <h3 className="font-semibold text-gray-900">
                        {formData.gradingScale === 'expectations' ? 'Performance Levels' : 'Custom Scale Builder'}
                      </h3>
                    </div>
                    {formData.gradingScale === 'custom' && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">Presets:</span>
                        <button
                          type="button"
                          onClick={() => handleApplyPreset('expectations')}
                          className="text-xs px-3 py-2 min-h-[44px] bg-white rounded border hover:bg-gray-50"
                        >
                          Expectations
                        </button>
                        <button
                          type="button"
                          onClick={() => handleApplyPreset('mastery')}
                          className="text-xs px-3 py-2 min-h-[44px] bg-white rounded border hover:bg-gray-50"
                        >
                          Mastery
                        </button>
                        <button
                          type="button"
                          onClick={() => handleApplyPreset('standards')}
                          className="text-xs px-3 py-2 min-h-[44px] bg-white rounded border hover:bg-gray-50"
                        >
                          Standards
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Scale Name (for custom) */}
                  {formData.gradingScale === 'custom' && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Scale Name</label>
                      <input
                        type="text"
                        value={formData.customScaleName}
                        onChange={(e) => setFormData({...formData, customScaleName: e.target.value})}
                        placeholder="e.g., Standards-Based Grading"
                        className="form-input mt-1"
                      />
                    </div>
                  )}

                  {/* Custom Levels */}
                  <div className="space-y-2">
                    {customLevels.map((level, index) => (
                      <div key={index} className="flex items-center space-x-2 bg-white p-3 rounded-lg">
                        <input
                          type="color"
                          value={level.color}
                          onChange={(e) => handleCustomLevelChange(index, 'color', e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer border-0"
                          title="Level color"
                        />
                        <input
                          type="text"
                          value={level.label}
                          onChange={(e) => handleCustomLevelChange(index, 'label', e.target.value)}
                          placeholder="Level name"
                          className="form-input flex-1"
                        />
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={level.min}
                            onChange={(e) => handleCustomLevelChange(index, 'min', e.target.value)}
                            className="form-input w-20"
                          />
                          <span className="text-gray-500 text-sm">% min</span>
                        </div>
                        <input
                          type="text"
                          value={level.description}
                          onChange={(e) => handleCustomLevelChange(index, 'description', e.target.value)}
                          placeholder="Description (optional)"
                          className="form-input flex-1 hidden md:block"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveCustomLevel(index)}
                          className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          disabled={customLevels.length <= 2}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {formData.gradingScale === 'custom' && (
                    <button
                      type="button"
                      onClick={handleAddCustomLevel}
                      className="px-3 py-2 min-h-[44px] text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 flex items-center rounded-lg"
                    >
                      <Plus size={16} className="mr-1" />
                      Add Level
                    </button>
                  )}

                  {/* Preview */}
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                    <div className="flex flex-wrap gap-2">
                      {customLevels
                        .filter(l => l.label.trim())
                        .sort((a, b) => b.min - a.min)
                        .map((level, i) => (
                          <div
                            key={i}
                            className="px-3 py-1 rounded-full text-sm font-medium"
                            style={{ backgroundColor: level.color + '20', color: level.color, border: `1px solid ${level.color}` }}
                          >
                            {level.label} ({level.min}%+)
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Passing Threshold */}
              <div>
                <label className="form-label">Passing Threshold (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.passingThreshold}
                  onChange={(e) => setFormData({...formData, passingThreshold: e.target.value})}
                  className="form-input w-32"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Minimum percentage required to pass
                </p>
              </div>

              {/* Grade Categories */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="form-label mb-0">Grade Categories</label>
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    className="px-3 py-2 min-h-[44px] text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 flex items-center rounded-lg"
                  >
                    <Plus size={16} className="mr-1" />
                    Add Category
                  </button>
                </div>

                <div className="space-y-2">
                  {formData.gradeCategories.map((category, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={category.name}
                        onChange={(e) => handleCategoryChange(index, 'name', e.target.value)}
                        placeholder="Category name"
                        className="form-input flex-1"
                      />
                      <input
                        type="number"
                        value={category.weight}
                        onChange={(e) => handleCategoryChange(index, 'weight', parseInt(e.target.value) || 0)}
                        placeholder="Weight %"
                        className="form-input w-24"
                      />
                      <span className="text-gray-500">%</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCategory(index)}
                        className="p-2 text-red-400 hover:text-red-600"
                        disabled={formData.gradeCategories.length <= 1}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className={`mt-2 text-sm ${totalWeight === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                  Total Weight: {totalWeight}% {totalWeight !== 100 && '(should equal 100%)'}
                </div>
              </div>

              {/* Display Options */}
              <div className="space-y-3">
                <label className="form-label">Display Options</label>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="showLetterGrade"
                    checked={formData.showLetterGrade}
                    onChange={(e) => setFormData({...formData, showLetterGrade: e.target.checked})}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <label htmlFor="showLetterGrade">
                    {formData.gradingScale === 'expectations' ? 'Show expectation level (Exceeding/Meeting/Not Meeting)' : 
                     formData.gradingScale === 'custom' ? `Show ${formData.customScaleName || 'custom'} level` : 
                     'Show letter grades'}
                  </label>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="showPercentage"
                    checked={formData.showPercentage}
                    onChange={(e) => setFormData({...formData, showPercentage: e.target.checked})}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <label htmlFor="showPercentage">Show percentages</label>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="showRank"
                    checked={formData.showRank}
                    onChange={(e) => setFormData({...formData, showRank: e.target.checked})}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <label htmlFor="showRank">Show class rank</label>
                </div>
              </div>

              {/* Parent Visibility */}
              <div className="space-y-3">
                <label className="form-label">Parent Portal</label>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="gradesVisibleToParents"
                    checked={formData.gradesVisibleToParents}
                    onChange={(e) => setFormData({...formData, gradesVisibleToParents: e.target.checked})}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <label htmlFor="gradesVisibleToParents">Grades visible to parents</label>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="showGradesImmediately"
                    checked={formData.showGradesImmediately}
                    onChange={(e) => setFormData({...formData, showGradesImmediately: e.target.checked})}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <label htmlFor="showGradesImmediately">Show grades immediately (uncheck to manually release)</label>
                </div>
              </div>

              {/* Reporting Period */}
              <div>
                <label className="form-label">Default Reporting Period</label>
                <select
                  value={formData.reportingPeriod}
                  onChange={(e) => setFormData({...formData, reportingPeriod: e.target.value})}
                  className="form-input"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semester">Semester</option>
                </select>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formData.gradingEnabled && totalWeight !== 100}
              className="btn-primary disabled:opacity-50"
            >
              {createSettings.isPending || updateSettings.isPending ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                isEditing ? 'Save Changes' : 'Enable Grading'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GradingSettingsModal;
